import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Bot, Loader2, Send } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

/* Frag dein Business (v8 M19 d): fertige Auswertungen auf Knopfdruck, mit KI zusätzlich ein Fragefeld.
   Alles nur lesend; die KI sieht Personen nur als Platzhalter. */

const fehler = (e, std) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : std);

function Zeile({ z }) {
  const inhalt = (
    <div className="flex items-start justify-between gap-3 py-2">
      <span className="text-sm font-medium">{z.text}</span>
      {z.wert && <span className="text-sm text-muted-foreground text-right shrink-0 max-w-[55%]">{z.wert}</span>}
    </div>
  );
  return z.link
    ? <Link to={z.link} className="block border-b border-border last:border-0 hover:bg-muted/50 -mx-2 px-2 rounded">{inhalt}</Link>
    : <div className="border-b border-border last:border-0">{inhalt}</div>;
}

export default function CopilotPage() {
  const [info, setInfo] = useState(null);
  const [aktiv, setAktiv] = useState('');
  const [ergebnis, setErgebnis] = useState(null);
  const [busy, setBusy] = useState('');
  const [text, setText] = useState('');
  const [antwort, setAntwort] = useState(null);

  useEffect(() => {
    api.get('/frag/werkzeuge').then(r => setInfo(r.data)).catch(e => { toast.error(fehler(e, 'Nicht erreichbar')); setInfo({ werkzeuge: [], ki: false }); });
  }, []);

  const zeigen = async (key) => {
    setAktiv(key); setBusy('werkzeug');
    try { setErgebnis((await api.get(`/frag/werkzeug/${key}`)).data); }
    catch (e) { toast.error(fehler(e, 'Auswertung fehlgeschlagen')); }
    setBusy('');
  };
  const fragen = async (e) => {
    e.preventDefault();
    if (text.trim().length < 3) return;
    setBusy('frage');
    try { setAntwort((await api.post('/frag/frage', { text })).data); }
    catch (err) { toast.error(fehler(err, 'Frage nicht beantwortet')); }
    setBusy('');
  };

  if (!info) return <div className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-5" data-testid="copilot-page">
      <div>
        <h1 className="text-2xl font-extrabold flex items-center gap-2"><Bot className="w-6 h-6 text-marke" /> Frag dein Business</h1>
        <p className="text-sm text-muted-foreground">Schnelle Antworten aus deinen Daten. Es wird nichts verändert.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {info.werkzeuge.map(w => (
          <button key={w.key} onClick={() => zeigen(w.key)} disabled={busy === 'werkzeug'}
            className={`rounded-full border px-3 py-1.5 text-sm text-left ${aktiv === w.key ? 'border-marke bg-marke/10 text-marke font-semibold' : 'border-border'}`}>{w.frage}</button>
        ))}
      </div>

      {busy === 'werkzeug' && <div className="flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
      {ergebnis && busy !== 'werkzeug' && (
        <Card><CardContent className="p-4">
          <h2 className="font-bold mb-2">{ergebnis.titel}</h2>
          {ergebnis.zeilen.length === 0
            ? <p className="text-sm text-muted-foreground">{ergebnis.leer}</p>
            : ergebnis.zeilen.map((z, i) => <Zeile key={i} z={z} />)}
        </CardContent></Card>
      )}

      {info.ki && (
        <div className="space-y-3">
          <form onSubmit={fragen} className="flex gap-2">
            <input value={text} onChange={e => setText(e.target.value)} maxLength={300} placeholder="Frag etwas …"
              className="flex-1 min-w-0 rounded-md border border-border bg-background px-3 py-2 text-sm" />
            <Button type="submit" className="bg-marke text-white hover:bg-marke-dunkel" disabled={busy === 'frage' || text.trim().length < 3}>
              {busy === 'frage' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
          <p className="text-xs text-muted-foreground">Bitte keine Namen eintippen: Die Frage geht an die KI. Namen in der Antwort setzt das CRM selbst ein, die KI sieht nur Platzhalter.</p>
          {antwort && (
            <Card><CardContent className="p-4 space-y-3">
              <p className="text-sm whitespace-pre-wrap">{antwort.antwort}</p>
              {antwort.links?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {antwort.links.map((l, i) => <Link key={i} to={l.link} className="rounded-full border border-border px-3 py-1 text-xs hover:bg-muted">{l.text}</Link>)}
                </div>
              )}
            </CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}
